import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'next-i18next/pages'
import { DeliveryAddressInputs } from 'store/customer/types'
import { DeliveryAddressFieldKeys, InputGroupKeys } from 'store/customer/enums'
import FormButtons from '../../components/form-buttons/FormButtons'
import { useRouter } from 'next/router'
import { EventTrackingParameters } from 'types/event-tracking-parameters'
import { useBeforeunload } from 'react-beforeunload'
import { checkIsEavOrFixed } from 'utils/customerFlow'
import { updateGroupInputs, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { setPostHogPersonProperties, trackEvent, trackGTMEvent } from 'utils/tracking'
import { Banner, Button, FieldLabel, Form, Toggle } from '@boltenergy-be/design-system'
import { Address as AddressInput } from 'components/inputs'
import { selectIsBoltDriveFlexFlow } from 'features/bolt-drive-flex/selectors'
import { StepProps } from 'features/registration/steps/types'
import { IDENTIFICATION_METHODS, CUSTOMER_TYPES, REGIONS, SIMULATION_TYPES, PRODUCTS, Product } from '@boltenergy-be/shared'
import { CommonTrackingParams } from 'types/tracking'
import { checkEnforcePlenty } from 'features/simulation/utils'
import { MarketingDataKeys } from 'store/marketing/types'
import { checkProductIsAvailableInRegion } from 'utils/products'
import { setLogContext } from 'utils/logging'
import { useGetTownsQuery } from 'store/api/bolt-api'
import { POSTAL_CODE } from 'constants/regex'

const DeliveryAddress = ({ nextStep, producer, saveAndResimulate, producerOverCapacity }: StepProps) => {
  // Redux store
  const marketingStore = useStoreSelector((store) => store.marketing)
  const isBoltDriveFlexFlow = selectIsBoltDriveFlexFlow(marketingStore)
  const { availableProducts } = marketingStore
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const { personalData, simulation, deliveryAddress } = inputs
  const dispatch = useStoreDispatch()

  // Next router
  const { locale, back } = useRouter()

  // React hook form
  const hookForm = useForm<DeliveryAddressInputs>({
    defaultValues: {
      ...deliveryAddress
    },
    mode: 'onChange'
  })
  const {
    formState: { isDirty },
    handleSubmit,
    setValue,
    control,
    getValues
  } = hookForm
  const watchNeedsGas = useWatch({ control, name: 'needsGas' })
  const watchPostalCode: string = useWatch({ control, name: 'address.postalCode' })

  // i18n
  const { t } = useTranslation(['registration', 'commonFormFields', 'common', 'validation'])

  // Redux query
  const { currentData: townsData, isFetching: isFetchingTowns } = useGetTownsQuery(watchPostalCode, {
    skip: !POSTAL_CODE.test(watchPostalCode ?? '')
  })

  // Constants
  const region = townsData?.region ?? simulation.region
  const enforcePlenty = checkEnforcePlenty({
    region,
    availableProducts: marketingStore[MarketingDataKeys.AVAILABLE_PRODUCTS]
  })
  const productAvailableInRegion = checkProductIsAvailableInRegion(region, propositions.selectedProposition, availableProducts)
  const isExpressRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express
  const isEavOrFixed = checkIsEavOrFixed(inputs.simulation.chosenSimulationType)
  const noSimulation = isEavOrFixed
  const boltFlexDriveNotAvailableForRegion = isBoltDriveFlexFlow && region !== REGIONS.flanders
  const currentProductNotAvailableInPlentyEnforce =
    enforcePlenty && !([PRODUCTS.plentyVariableOffline, PRODUCTS.plentyFix] as Product[]).includes(propositions.selectedProposition)
  const productNotAvailableForRegion =
    !productAvailableInRegion || boltFlexDriveNotAvailableForRegion || currentProductNotAvailableInPlentyEnforce
  const showExtraFields = isExpressRegistration || isEavOrFixed

  /**
   * Triggered everytime the component unloads
   * Prevents unload if one of the form fields is changed
   */
  useBeforeunload((e) => {
    if (isDirty) e.preventDefault()
  })

  /**
   * Handles the form submit after validation
   *
   * @param {PersonalDataInputs} data
   */
  const onSubmit = (data: DeliveryAddressInputs) => {
    setLogContext('delivery_address_form_input', data)

    // Leave function if producer is over capacity
    if (producerOverCapacity) {
      return
    }

    dispatch(
      updateGroupInputs({
        group: InputGroupKeys.DELIVERY_ADDRESS,
        inputs: data
      })
    )

    // ItsMe is temporary disable due to acquisition block project.
    // Check if itsme identification should be used
    // const isItsmeFlow = checkShouldIdentifyWithItsme(simulation.region, personalData.isCompany, marketingStore)
    // if (isItsmeFlow && !eventID && !noEanFlow) {
    //   dispatch(setFlowAlternative({ key: FlowAlternatives.ITSME, value: true }))
    // } else {
    //   dispatch(setFlowAlternative({ key: FlowAlternatives.ITSME, value: false }))
    // }
    //
    // dispatch(
    //   updateMarketingData({
    //     key: MarketingDataKeys.IDENTIFICATION_METHOD,
    //     value: isItsmeFlow ? IDENTIFICATION_METHODS.itsme : IDENTIFICATION_METHODS.default
    //   })
    // )

    // Store region and customer type for the current anonymous person and A/B test.
    setPostHogPersonProperties({
      [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential,
      [CommonTrackingParams.REGION]: simulation.region,
      [CommonTrackingParams.IDENTIFICATION_METHOD]: IDENTIFICATION_METHODS.default
    })

    // Track 'Subscription step finished' event @Semetis
    trackGTMEvent('subscription_step_finished:delivery-address', {
      bolt_product: propositions.selectedProposition,
      costumer_type: personalData.isCompany ? 'company' : 'private',
      postcode: data.address.postalCode,
      producer_name: producer.name[locale]
    } satisfies EventTrackingParameters)
    trackEvent(
      { category: 'subscription', object: 'delivery_address_step', action: 'finish' },
      {
        [CommonTrackingParams.IDENTIFICATION_METHOD]: IDENTIFICATION_METHODS.default
      }
    )

    nextStep()
  }

  /**
   * Handles the energy type change
   *
   * @param {boolean} needsGas
   */
  const handleEnergyTypeChange = (needsGas: boolean) => {
    if (noSimulation) {
      dispatch(
        updateInput({
          group: InputGroupKeys.DELIVERY_ADDRESS,
          key: DeliveryAddressFieldKeys.NEEDS_GAS,
          value: needsGas
        })
      )
    } else {
      saveAndResimulate(InputGroupKeys.DELIVERY_ADDRESS, DeliveryAddressFieldKeys.NEEDS_GAS, needsGas)
    }
  }

  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {showExtraFields && (
          <div className="grid-col-full">
            <FieldLabel htmlFor="needsGas">{t('personalData.sections.energyType')}</FieldLabel>
            <Toggle
              id="needsGas"
              isFullwidth
              active={watchNeedsGas}
              onClick={(needsGas: boolean) => {
                setValue('needsGas', needsGas as never)
                handleEnergyTypeChange(needsGas)
              }}
              options={[
                { label: t('common:electricity'), value: false },
                { label: t('common:electricityAndGas'), value: true }
              ]}
            />
          </div>
        )}

        <Form.Fieldset title={t('personalData.sections.address')}>
          <AddressInput parentDotPath="address" />

          {productNotAvailableForRegion && (
            <Banner type="blocking" className="grid-col-full">
              {boltFlexDriveNotAvailableForRegion ? (
                t('common:driveFlexWarnings.region')
              ) : (
                <Trans
                  t={t}
                  ns="common"
                  i18nKey="productNotAvailable"
                  components={[
                    <Button
                      type="button"
                      className="weight-semibold"
                      representation="link"
                      variant="inline"
                      onClick={() => {
                        // Save address inputs
                        const address: Partial<DeliveryAddressInputs['address']> = getValues('address', { dirtyFields: true })
                        dispatch(
                          updateInput({
                            group: InputGroupKeys.DELIVERY_ADDRESS,
                            key: DeliveryAddressFieldKeys.ADDRESS,
                            value: {
                              ...deliveryAddress.address,
                              ...address
                            }
                          })
                        )

                        back()
                      }}
                    />
                  ]}
                />
              )}
            </Banner>
          )}
        </Form.Fieldset>

        <Form.Footer>
          <FormButtons isLoading={isFetchingTowns} isDisabled={productNotAvailableForRegion} />
        </Form.Footer>
      </Form>
    </FormProvider>
  )
}

export default DeliveryAddress
